import React, { useEffect, useMemo, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faDollarSign, faEdit, faPlus, faSyncAlt } from "@fortawesome/free-solid-svg-icons";
import Swal from "sweetalert2";
import localForage from "localforage";
import { buildApiUrl, useBaseUrl } from "../../../utils/utils";
import ServiceCenterShell from "./ServiceCenterShell";

interface Service {
    id: number;
    service_id: number;
    service_title: string;
    service_subtitle: string;
    service_category: string;
    minimum_rate: number;
    hourly_rate: number;
    is_billable: boolean;
    is_active: boolean;
}

interface AvailableService {
    id: number;
    title: string;
    subtitle: string;
}

interface PaginationMeta {
    currentPage: number;
    totalPages: number;
    totalItems: number;
    hasNext: boolean;
    hasPrevious: boolean;
}

interface PricingSummary {
    active_services: number;
    inactive_services: number;
    billable_services: number;
    average_minimum: number;
    average_hourly: number;
}

const formatCurrency = (value: number) => `KES ${Number(value || 0).toLocaleString()}`;
const emptyPagination = (): PaginationMeta => ({ currentPage: 1, totalPages: 1, totalItems: 0, hasNext: false, hasPrevious: false });
const emptySummary = (): PricingSummary => ({ active_services: 0, inactive_services: 0, billable_services: 0, average_minimum: 0, average_hourly: 0 });
const readPagination = (pagination: any, fallbackLength: number = 0): PaginationMeta => ({
    currentPage: Number(pagination?.current_page ?? pagination?.currentPage ?? 1),
    totalPages: Number(pagination?.total_pages ?? pagination?.totalPages ?? 1),
    totalItems: Number(pagination?.total_items ?? pagination?.totalItems ?? fallbackLength),
    hasNext: Boolean(pagination?.has_next ?? pagination?.hasNext ?? false),
    hasPrevious: Boolean(pagination?.has_previous ?? pagination?.hasPrevious ?? false),
});

const PricingPage: React.FC = () => {
    const baseUrl = useBaseUrl();
    const [clinicId, setClinicId] = useState<number | null>(null);
    const [services, setServices] = useState<Service[]>([]);
    const [availableServices, setAvailableServices] = useState<AvailableService[]>([]);
    const [servicesPagination, setServicesPagination] = useState<PaginationMeta>(emptyPagination());
    const [pricingSummary, setPricingSummary] = useState<PricingSummary>(emptySummary());
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState("");
    const [servicePage, setServicePage] = useState(1);
    const [availableServiceSearch, setAvailableServiceSearch] = useState("");
    const [showAddModal, setShowAddModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [editingService, setEditingService] = useState<Service | null>(null);
    const [formData, setFormData] = useState({
        service_id: "",
        minimum_rate: "",
        hourly_rate: "",
        is_billable: true,
    });

    useEffect(() => {
        document.title = "Service Pricing - Uzima Healthcare";
        getClinicId();
    }, []);

    useEffect(() => {
        if (!clinicId) return;
        FetchPricingServices(servicePage);
    }, [clinicId, baseUrl, servicePage, search]);

    useEffect(() => {
        setServicePage(1);
    }, [search]);

    useEffect(() => {
        if (!clinicId || (!showAddModal && !showEditModal)) return;
        FetchAvailablePricingServices();
    }, [clinicId, baseUrl, showAddModal, showEditModal, availableServiceSearch]);

    const getClinicId = async () => {
        try {
            const storedClinicId = await localForage.getItem("clinic_id");
            if (storedClinicId) {
                setClinicId(storedClinicId as number);
                return;
            }
            const loginInfo: any = await localForage.getItem("uzima_login_info");
            if (loginInfo?.clinic_id) setClinicId(loginInfo.clinic_id);
        } catch (error) {
            console.error("Error getting clinic ID:", error);
        }
    };

    const FetchPricingServices = async (page: number = 1) => {
        if (!clinicId) return;
        try {
            setLoading(true);
            const params = new URLSearchParams({ page: String(page), page_size: '12', billable: 'true' });
            if (search) params.set('search', search);
            const response = await fetch(buildApiUrl(baseUrl, `facilities/services/clinic-services/${clinicId}/?${params.toString()}`));
            if (!response.ok) {
                setServices([]);
                setServicesPagination(emptyPagination());
                setPricingSummary(emptySummary());
                return;
            }
            const data = await response.json();
            const payload = data.data || {};
            const serviceList = payload.services || [];
            setServices(Array.isArray(serviceList) ? serviceList : []);
            setServicesPagination(readPagination(payload.pagination, serviceList.length));
            setPricingSummary(payload.summary || emptySummary());
        } catch (error) {
            console.error("Error fetching pricing services:", error);
            setServices([]);
            setServicesPagination(emptyPagination());
            setPricingSummary(emptySummary());
        } finally {
            setLoading(false);
        }
    };

    const FetchAvailablePricingServices = async () => {
        if (!clinicId) return;
        try {
            const params = new URLSearchParams({ page: '1', page_size: '50' });
            if (availableServiceSearch) params.set('search', availableServiceSearch);
            const response = await fetch(buildApiUrl(baseUrl, `facilities/services/available/${clinicId}/?${params.toString()}`));
            if (!response.ok) {
                setAvailableServices([]);
                return;
            }
            const data = await response.json();
            const rawServices = Array.isArray(data.data?.available_services) ? data.data.available_services : [];
            setAvailableServices(rawServices.map((service: any) => ({
                id: service.id,
                title: service.title || service.name,
                subtitle: service.subtitle || "",
            })));
        } catch (error) {
            console.error("Error fetching available services:", error);
            setAvailableServices([]);
        }
    };

    const UpdatePricingFormInput = (event: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
        const { name, value, type } = event.target;
        setFormData((prev) => ({
            ...prev,
            [name]: type === "checkbox" ? (event.target as HTMLInputElement).checked : value,
        }));
    };

    const resetForm = () => {
        setFormData({ service_id: "", minimum_rate: "", hourly_rate: "", is_billable: true });
        setAvailableServiceSearch("");
        setEditingService(null);
    };

    const closeAddModal = () => {
        setShowAddModal(false);
        resetForm();
    };

    const closeEditModal = () => {
        setShowEditModal(false);
        resetForm();
    };

    const CreatePricingTier = async () => {
        if (!clinicId || !formData.service_id || !formData.minimum_rate || !formData.hourly_rate) {
            Swal.fire({ icon: "warning", title: "Missing information", text: "Please complete all pricing fields." });
            return;
        }
        try {
            const response = await fetch(buildApiUrl(baseUrl, "facilities/services/create/"), {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    ...formData,
                    clinic_id: clinicId,
                    minimum_rate: parseFloat(formData.minimum_rate),
                    hourly_rate: parseFloat(formData.hourly_rate),
                }),
            });
            const data = await response.json();
            if (data.status !== "success") throw new Error(data.message || "Failed to create pricing tier");
            Swal.fire({ icon: "success", title: "Pricing added", text: "Your pricing tier has been created.", timer: 1600, showConfirmButton: false });
            closeAddModal();
            FetchPricingServices(servicePage);
            FetchAvailablePricingServices();
        } catch (error) {
            console.error("Error adding pricing:", error);
            Swal.fire({ icon: "error", title: "Add failed", text: "Failed to add pricing tier. Please try again." });
        }
    };

    const SavePricingTierChanges = async () => {
        if (!clinicId || !editingService || !formData.service_id || !formData.minimum_rate || !formData.hourly_rate) {
            Swal.fire({ icon: "warning", title: "Missing information", text: "Please complete all pricing fields." });
            return;
        }
        try {
            const response = await fetch(buildApiUrl(baseUrl, `facilities/services/update/${editingService.id}/`), {
                method: "PUT",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    ...formData,
                    clinic_id: clinicId,
                    minimum_rate: parseFloat(formData.minimum_rate),
                    hourly_rate: parseFloat(formData.hourly_rate),
                }),
            });
            const data = await response.json();
            if (data.status !== "success") throw new Error(data.message || "Failed to update pricing tier");
            Swal.fire({ icon: "success", title: "Pricing updated", text: "Your pricing tier has been updated.", timer: 1600, showConfirmButton: false });
            closeEditModal();
            FetchPricingServices(servicePage);
        } catch (error) {
            console.error("Error updating pricing:", error);
            Swal.fire({ icon: "error", title: "Update failed", text: "Failed to update pricing tier. Please try again." });
        }
    };

    const openEditModal = (service: Service) => {
        setEditingService(service);
        setFormData({
            service_id: service.service_id.toString(),
            minimum_rate: service.minimum_rate.toString(),
            hourly_rate: service.hourly_rate.toString(),
            is_billable: service.is_billable,
        });
        setShowEditModal(true);
    };

    const summary = useMemo(() => {
        const highestMinimum = services.reduce((highest, service) => Math.max(highest, Number(service.minimum_rate || 0)), 0);
        const highestHourly = services.reduce((highest, service) => Math.max(highest, Number(service.hourly_rate || 0)), 0);
        return {
            highestMinimum,
            highestHourly,
            averageMinimum: pricingSummary.average_minimum,
            averageHourly: pricingSummary.average_hourly,
            billableCount: pricingSummary.billable_services,
        };
    }, [pricingSummary, services]);

    const renderPricingModal = (isEditing: boolean) => (
        <div className="sc-modal-overlay" onClick={isEditing ? closeEditModal : closeAddModal}>
            <div className="sc-modal" onClick={(event) => event.stopPropagation()}>
                <div className="sc-modal-header">
                    <h2>{isEditing ? "Edit pricing tier" : "Add pricing tier"}</h2>
                    <button className="sc-modal-close" onClick={isEditing ? closeEditModal : closeAddModal}>
                        <i className="bx bx-x"></i>
                    </button>
                </div>
                <div className="sc-modal-body">
                    <div className="sc-form-group">
                        <label>Search services</label>
                        <input className="sc-input" value={availableServiceSearch} onChange={(event) => setAvailableServiceSearch(event.target.value)} placeholder="Search available services..." />
                    </div>
                    <div className="sc-form-group">
                        <label>Service <span>*</span></label>
                        <select className="sc-input" name="service_id" value={formData.service_id} onChange={UpdatePricingFormInput}>
                            <option value="">Select service</option>
                            {availableServices.map((service) => (
                                <option key={service.id} value={service.id}>{service.title}{service.subtitle ? ` - ${service.subtitle}` : ""}</option>
                            ))}
                        </select>
                    </div>
                    <div className="sc-form-row">
                        <div className="sc-form-group">
                            <label>Minimum rate <span>*</span></label>
                            <input className="sc-input" type="number" min="0" step="0.01" name="minimum_rate" value={formData.minimum_rate} onChange={UpdatePricingFormInput} placeholder="0" />
                        </div>
                        <div className="sc-form-group">
                            <label>Hourly rate <span>*</span></label>
                            <input className="sc-input" type="number" min="0" step="0.01" name="hourly_rate" value={formData.hourly_rate} onChange={UpdatePricingFormInput} placeholder="0" />
                        </div>
                    </div>
                    <label className="sc-toggle">
                        <input type="checkbox" name="is_billable" checked={formData.is_billable} onChange={UpdatePricingFormInput} />
                        <span>Make this pricing tier billable</span>
                    </label>
                </div>
                <div className="sc-modal-footer">
                    <button className="sc-btn sc-btn-ghost" onClick={isEditing ? closeEditModal : closeAddModal}>Cancel</button>
                    <button className="sc-btn sc-btn-primary" onClick={isEditing ? SavePricingTierChanges : CreatePricingTier}>
                        <FontAwesomeIcon icon={isEditing ? faEdit : faPlus} /> {isEditing ? "Save pricing" : "Add pricing tier"}
                    </button>
                </div>
            </div>
        </div>
    );

    return (
        <>
            <ServiceCenterShell
                title="Pricing"
                description="Manage minimum and hourly rates directly from the pricing workspace without leaving the Service Center flow."
                activeTab="pricing"
                action={
                    <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                        <button className="sc-btn sc-btn-outline" onClick={() => FetchPricingServices(servicePage)}>
                            <FontAwesomeIcon icon={faSyncAlt} /> Refresh
                        </button>
                        <button className="sc-btn sc-btn-primary" onClick={() => setShowAddModal(true)}>
                            <FontAwesomeIcon icon={faPlus} /> Add Pricing Tier
                        </button>
                    </div>
                }
            >
                <div className="sc-stats">
                    <div className="sc-stat">
                        <div className="sc-stat-icon teal"><i className="bx bx-wallet"></i></div>
                        <div className="sc-stat-value">{formatCurrency(summary.averageMinimum)}</div>
                        <div className="sc-stat-label">Average Minimum</div>
                    </div>
                    <div className="sc-stat">
                        <div className="sc-stat-icon blue"><i className="bx bx-time-five"></i></div>
                        <div className="sc-stat-value">{formatCurrency(summary.averageHourly)}</div>
                        <div className="sc-stat-label">Average Hourly</div>
                    </div>
                    <div className="sc-stat">
                        <div className="sc-stat-icon amber"><i className="bx bx-trending-up"></i></div>
                        <div className="sc-stat-value">{formatCurrency(summary.highestMinimum)}</div>
                        <div className="sc-stat-label">Highest Minimum</div>
                    </div>
                    <div className="sc-stat">
                        <div className="sc-stat-icon violet"><i className="bx bx-stopwatch"></i></div>
                        <div className="sc-stat-value">{formatCurrency(summary.highestHourly)}</div>
                        <div className="sc-stat-label">Highest Hourly</div>
                    </div>
                    <div className="sc-stat">
                        <div className="sc-stat-icon rose"><i className="bx bx-receipt"></i></div>
                        <div className="sc-stat-value">{summary.billableCount}</div>
                        <div className="sc-stat-label">Billable Services</div>
                    </div>
                </div>

                <div className="sc-toolbar">
                    <div className="sc-search">
                        <i className="bx bx-search"></i>
                        <input placeholder="Search pricing by service or category..." value={search} onChange={(event) => setSearch(event.target.value)} />
                    </div>
                    <span className="sc-chip soft">Pricing changes save to your clinic service catalogue</span>
                </div>

                {loading ? (
                    <div className="sc-spinner"></div>
                ) : services.length === 0 ? (
                    <div className="sc-empty">
                        <i className="bx bx-dollar-circle"></i>
                        <h3>{servicesPagination.totalItems === 0 ? "No pricing available" : "No matching services"}</h3>
                        <p>{servicesPagination.totalItems === 0 ? "Create your first pricing tier to start publishing service rates." : "Try a broader search term to view more pricing entries."}</p>
                        {servicesPagination.totalItems === 0 && <button className="sc-btn sc-btn-primary" onClick={() => setShowAddModal(true)}><FontAwesomeIcon icon={faPlus} /> Add first pricing tier</button>}
                    </div>
                ) : (
                    <>
                        <div className="sc-panel">
                            <div className="sc-panel-head">
                                <div>
                                    <h3>Pricing matrix</h3>
                                    <p>Create, review, and edit minimum and hourly rates for each clinic service.</p>
                                </div>
                            </div>
                            <div className="sc-panel-body">
                                <div className="sc-table-wrap">
                                    <table className="sc-table">
                                        <thead>
                                            <tr>
                                                <th>Service</th>
                                                <th>Category</th>
                                                <th>Minimum</th>
                                                <th>Hourly</th>
                                                <th>Billing</th>
                                                <th></th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {services.map((service) => (
                                                <tr key={service.id}>
                                                    <td>
                                                        <strong>{service.service_title}</strong>
                                                        <div style={{ fontSize: "0.74rem", color: "var(--uzima-shell-text-muted)", marginTop: 4 }}>{service.service_subtitle || "No subtitle available"}</div>
                                                    </td>
                                                    <td>{service.service_category || "General"}</td>
                                                    <td>{formatCurrency(service.minimum_rate)}</td>
                                                    <td>{formatCurrency(service.hourly_rate)}</td>
                                                    <td><span className={`sc-chip ${service.is_billable ? "" : "soft"}`}>{service.is_billable ? "Billable" : "Non-billable"}</span></td>
                                                    <td>
                                                        <button className="sc-btn sc-btn-ghost sc-btn-sm" onClick={() => openEditModal(service)}>
                                                            <FontAwesomeIcon icon={faEdit} /> Edit
                                                        </button>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </div>
                        {servicesPagination.totalPages > 1 && (
                            <div style={{ display: "flex", justifyContent: "center", alignItems: "center", gap: 12, marginTop: 20 }}>
                                <button className="sc-btn sc-btn-ghost" disabled={!servicesPagination.hasPrevious} onClick={() => setServicePage((prev) => prev - 1)}>Prev</button>
                                <span className="sc-chip soft">Page {servicesPagination.currentPage} of {servicesPagination.totalPages}</span>
                                <button className="sc-btn sc-btn-ghost" disabled={!servicesPagination.hasNext} onClick={() => setServicePage((prev) => prev + 1)}>Next</button>
                            </div>
                        )}
                    </>
                )}
            </ServiceCenterShell>
            {showAddModal && renderPricingModal(false)}
            {showEditModal && editingService && renderPricingModal(true)}
        </>
    );
};

export default PricingPage;
